'use client'
import { useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  Activity,
  Stethoscope,
  Pill,
  HeartPulse,
  CalendarClock,
  FlaskConical,
  ShieldAlert,
  ChevronRight,
  Loader2,
  IdCard,
  Droplet,
  Lock,
} from 'lucide-react'

import { useClinicalSummary } from '@/hooks/useClinicalSummary'
import { useClinicalRules } from '@/hooks/useClinicalRules'
import { useActivePatient } from '@/context/ActivePatientContext'
import { useClinicFormat } from '@/context/ClinicTimezoneContext'
import { EvolutionStatusBadge } from '@/components/consultation/EvolutionStatus'
import { VitalsTrendChart } from '@/components/consultation/VitalsTrendChart'
import { getInitials } from '@/lib/utils'
import type { EvolutionStatus } from '@/types'

const genderLabels: Record<string, string> = {
  male: 'Masculino',
  female: 'Femenino',
  non_binary: 'No binario',
  prefer_not_to_say: 'No especificado',
  other: 'Otro',
}

const labStatusLabels: Record<string, string> = {
  ordered: 'Solicitado',
  sample_collected: 'Muestra tomada',
  in_progress: 'En proceso',
}

const apptTypeLabels: Record<string, string> = {
  consultation: 'Consulta',
  follow_up: 'Seguimiento',
  emergency: 'Urgencia',
  routine_checkup: 'Chequeo de rutina',
  vaccination: 'Vacunación',
  lab_test: 'Laboratorio',
  imaging: 'Imagenología',
  procedure: 'Procedimiento',
  telemedicine: 'Telemedicina',
}

interface ClinicalSummaryOverviewProps {
  patientId: string
}

/**
 * Read-only, auto-updated clinical overview shown in the "Resumen" tab.
 *
 * Everything here is derived on read from signed notes / locked records (via the
 * backend clinical-summary endpoint) plus the patient record demographics. It is
 * never directly editable; clicking a section jumps to the full source tab.
 */
export function ClinicalSummaryOverview({ patientId }: ClinicalSummaryOverviewProps) {
  const navigate = useNavigate()
  const { formatDate, formatDateTime } = useClinicFormat()
  const { activePatientData: patient } = useActivePatient()
  const { data: summary, isLoading, isError } = useClinicalSummary(patientId)
  const { useAlerts } = useClinicalRules()
  const { data: alertsResp } = useAlerts({ patient_id: patientId })

  const go = (tab: string) => navigate(`/workspace/patient/${patientId}/${tab}`)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-ml-primary" />
      </div>
    )
  }

  if (isError || !summary || !patient) {
    return (
      <div className="rounded-2xl border border-ml-outline-variant/15 bg-ml-surface-container-lowest p-8 text-center text-sm text-ml-on-surface-variant/70">
        No se pudo cargar el resumen clínico.
      </div>
    )
  }

  const allergies = (summary.allergies ?? []).filter((a) => a?.name)
  const severeAllergies = allergies.filter((a) => a.severity === 'severe')
  const activeAlerts = (alertsResp?.items ?? []).filter(
    (a) => a.status === 'new' || a.status === 'acknowledged',
  )
  const vitals = summary.latest_vitals
  const bp =
    vitals?.systolic_bp != null && vitals?.diastolic_bp != null
      ? `${Math.round(vitals.systolic_bp)}/${Math.round(vitals.diastolic_bp)}`
      : null
  const enc = summary.last_encounter
  const next = summary.pending_orders?.next_appointment
  const pendingLabs = summary.pending_orders?.pending_labs ?? []

  return (
    <div className="animate-fade-in space-y-5">
      {/* Read-only banner */}
      <div className="flex items-center gap-2 rounded-xl border border-ml-outline-variant/15 bg-ml-surface-container-low px-4 py-2.5 text-[11px] text-ml-on-surface-variant/80">
        <Lock className="h-3.5 w-3.5 shrink-0 text-ml-primary" />
        <span>
          Resumen clínico · <strong>solo lectura</strong> · se actualiza automáticamente desde
          notas firmadas y registros cerrados
          {summary.derived_from_note_count > 0 && (
            <> · {summary.derived_from_note_count} nota(s) firmada(s)</>
          )}
        </span>
      </div>

      {/* ── 1 · Mini identification ──────────────────────────────────── */}
      <Section
        icon={IdCard}
        title="Identificación"
        onNavigate={() => go('datos-paciente')}
        navLabel="Datos del paciente"
      >
        <div className="flex items-center gap-4">
          {patient.photo_url ? (
            <img
              src={patient.photo_url}
              alt={patient.full_name}
              className="h-14 w-14 shrink-0 rounded-xl object-cover"
            />
          ) : (
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-ml-primary/10 text-base font-bold text-ml-primary">
              {getInitials(patient.first_name, patient.last_name)}
            </span>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-lg font-headline italic text-ml-on-surface">
              {patient.full_name}
            </p>
            <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-ml-on-surface-variant">
              <span>{patient.age != null ? `${patient.age} años` : '—'}</span>
              <span>· {genderLabels[patient.gender] ?? patient.gender}</span>
              <span className="font-mono text-xs">· Exp. {patient.mrn}</span>
            </p>
          </div>
          <div className="flex items-center gap-1.5 rounded-lg bg-ml-surface-container-high px-3 py-1.5">
            <Droplet className="h-4 w-4 text-ml-error" />
            <span className="text-sm font-bold text-ml-on-surface">{patient.blood_type || '—'}</span>
          </div>
        </div>

        {allergies.length > 0 ? (
          <div className="mt-3 flex items-start gap-2 rounded-xl border border-ml-error/25 bg-ml-error/10 px-3 py-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-ml-error" />
            <div className="min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-wider text-ml-error">
                Alergias conocidas
              </p>
              <p className="text-sm text-ml-on-surface">
                {allergies
                  .map((a) => a.name + (a.reaction ? ` (${a.reaction})` : ''))
                  .join(' · ')}
              </p>
            </div>
          </div>
        ) : (
          <p className="mt-3 text-xs text-ml-on-surface-variant/60">Sin alergias conocidas</p>
        )}
      </Section>

      {/* ── 2 · Current clinical status ──────────────────────────────── */}
      <Section
        icon={Activity}
        title="Estado clínico actual"
        onNavigate={() => go('consulta')}
        navLabel="Evolución"
        action={
          <EvolutionStatusBadge status={(summary.trend_status as EvolutionStatus | null) ?? null} />
        }
      >
        <div className="space-y-4">
          <div>
            <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/60">
              Diagnóstico(s) activo(s) · última nota firmada
            </p>
            {summary.active_problems.length > 0 ? (
              <ul className="space-y-1.5">
                {summary.active_problems.map((p) => (
                  <li
                    key={p.icd10_code}
                    className="flex items-center gap-2.5 rounded-xl border border-emerald-200/60 bg-emerald-50/40 px-3 py-2"
                  >
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" aria-hidden />
                    <span className="rounded-md bg-emerald-600/10 px-1.5 py-0.5 font-mono text-xs font-bold text-emerald-800">
                      {p.icd10_code}
                    </span>
                    {p.display_text && (
                      <span className="text-sm text-ml-on-surface">{p.display_text}</span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-ml-on-surface-variant/60">
                Sin diagnósticos en notas firmadas
              </p>
            )}
          </div>
          <div>
            <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/60">
              Condiciones crónicas activas
            </p>
            {summary.chronic_conditions.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {summary.chronic_conditions.map((c) => (
                  <span
                    key={c}
                    className="rounded border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-900"
                  >
                    {c}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-ml-on-surface-variant/60">Ninguna registrada</p>
            )}
          </div>
        </div>
      </Section>

      {/* ── 3 · Last visit ───────────────────────────────────────────── */}
      <Section
        icon={Stethoscope}
        title="Última visita"
        onNavigate={() =>
          enc
            ? navigate(
                `/workspace/patient/${patientId}/consulta?highlight=${enc.consultation_id}`,
              )
            : go('consulta')
        }
        navLabel="Evolución"
      >
        {enc ? (
          <div className="space-y-1.5 text-sm">
            <p className="flex flex-wrap items-center gap-x-3 text-ml-on-surface">
              <span className="font-medium">
                {enc.encounter_date ? formatDate(enc.encounter_date) : 'Fecha no registrada'}
              </span>
              {enc.doctor_name && (
                <span className="text-ml-on-surface-variant">· {enc.doctor_name}</span>
              )}
            </p>
            {enc.chief_complaint && (
              <p className="text-ml-on-surface-variant">
                <span className="text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/60">
                  Motivo:{' '}
                </span>
                {enc.chief_complaint}
              </p>
            )}
            {(enc.assessment || enc.plan_notes) && (
              <p className="line-clamp-2 text-ml-on-surface">
                <span className="text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/60">
                  Se decidió:{' '}
                </span>
                {enc.assessment || enc.plan_notes}
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-ml-on-surface-variant/60">Sin visitas firmadas</p>
        )}
      </Section>

      {/* ── 4 · Current treatment ────────────────────────────────────── */}
      <Section
        icon={Pill}
        title="Tratamiento actual"
        onNavigate={() => go('historia-clinica')}
        navLabel="Historia clínica"
      >
        {summary.active_medications.length > 0 ? (
          <ul className="space-y-2">
            {summary.active_medications.map((m) => (
              <li
                key={m.prescription_id}
                className="rounded-xl border border-ml-outline-variant/15 bg-ml-surface px-3 py-2"
              >
                <p className="text-sm font-semibold text-ml-on-surface">{m.medication_name}</p>
                <p className="text-xs text-ml-on-surface-variant">
                  {[m.dosage, m.frequency, m.route].filter(Boolean).join(' · ')}
                  {m.duration ? ` · ${m.duration}` : ''}
                </p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-ml-on-surface-variant/60">Sin medicación activa</p>
        )}
      </Section>

      {/* ── 5 · Most recent vital signs + mini trend ─────────────────── */}
      <Section
        icon={HeartPulse}
        title="Signos vitales recientes"
        onNavigate={() => go('consulta')}
        navLabel="Evolución"
        action={
          vitals?.recorded_at ? (
            <span className="text-[11px] text-ml-on-surface-variant/70">
              {formatDate(vitals.recorded_at)}
            </span>
          ) : undefined
        }
      >
        {vitals ? (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <VitalTile label="Presión arterial" value={bp} unit="mmHg" />
              <VitalTile
                label="Frec. cardíaca"
                value={vitals.heart_rate != null ? String(Math.round(vitals.heart_rate)) : null}
                unit="lpm"
              />
              <VitalTile
                label="Peso"
                value={vitals.weight_kg != null ? String(vitals.weight_kg) : null}
                unit="kg"
              />
              <VitalTile
                label="Glucosa"
                value={
                  vitals.glucose_mg_dl != null ? String(Math.round(vitals.glucose_mg_dl)) : null
                }
                unit="mg/dL"
              />
            </div>
            <div className="mt-4">
              <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/60">
                Tendencia (últimas lecturas firmadas)
              </p>
              <VitalsTrendChart
                patientId={patientId}
                onOpenNote={(consultationId) =>
                  navigate(
                    `/workspace/patient/${patientId}/consulta?highlight=${consultationId}`,
                  )
                }
              />
            </div>
          </>
        ) : (
          <p className="text-sm text-ml-on-surface-variant/60">
            Sin signos vitales en notas firmadas
          </p>
        )}
      </Section>

      {/* ── 6 · Pending next steps ───────────────────────────────────── */}
      <Section icon={CalendarClock} title="Próximos pasos pendientes">
        <div className="space-y-3">
          <div>
            <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/60">
              Próxima cita
            </p>
            {next ? (
              <button
                type="button"
                onClick={() => go('historial-citas')}
                className="flex w-full items-center justify-between rounded-xl border border-ml-outline-variant/15 bg-ml-surface px-3 py-2 text-left transition hover:bg-ml-surface-container-high"
              >
                <span className="text-sm text-ml-on-surface">
                  <span className="font-medium">{formatDateTime(next.appointment_date)}</span>
                  {next.appointment_type && (
                    <span className="text-ml-on-surface-variant">
                      {' · '}
                      {apptTypeLabels[next.appointment_type] ?? next.appointment_type}
                    </span>
                  )}
                  {next.reason ? ` · ${next.reason}` : ''}
                  {next.doctor_name ? ` · ${next.doctor_name}` : ''}
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-ml-on-surface-variant/50" />
              </button>
            ) : (
              <p className="text-sm text-ml-on-surface-variant/60">Sin citas programadas</p>
            )}
          </div>
          <div>
            <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/60">
              Estudios solicitados sin resultado
            </p>
            {pendingLabs.length > 0 ? (
              <ul className="space-y-1.5">
                {pendingLabs.map((l) => (
                  <li key={l.lab_result_id}>
                    <button
                      type="button"
                      onClick={() => go('laboratorios')}
                      className="flex w-full items-center justify-between rounded-xl border border-ml-outline-variant/15 bg-ml-surface px-3 py-2 text-left transition hover:bg-ml-surface-container-high"
                    >
                      <span className="flex items-center gap-2 text-sm text-ml-on-surface">
                        <FlaskConical className="h-4 w-4 text-ml-primary" />
                        {l.test_name}
                        <span className="rounded bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-900">
                          {labStatusLabels[l.status] ?? l.status}
                        </span>
                      </span>
                      <ChevronRight className="h-4 w-4 shrink-0 text-ml-on-surface-variant/50" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-ml-on-surface-variant/60">Sin estudios pendientes</p>
            )}
          </div>
        </div>
      </Section>

      {/* ── 7 · Clinical alerts / flags ──────────────────────────────── */}
      <Section icon={ShieldAlert} title="Alertas clínicas">
        {severeAllergies.length === 0 && activeAlerts.length === 0 ? (
          <p className="text-sm text-ml-on-surface-variant/60">
            Sin alertas que requieran atención inmediata
          </p>
        ) : (
          <ul className="space-y-2">
            {severeAllergies.map((a, i) => (
              <li
                key={`allergy-${a.name}-${i}`}
                className="flex items-start gap-2 rounded-xl border border-ml-error/30 bg-ml-error/10 px-3 py-2"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-ml-error" />
                <div>
                  <p className="text-sm font-semibold text-ml-error">
                    Alergia severa: {a.name}
                  </p>
                  {a.reaction && (
                    <p className="text-xs text-ml-on-surface-variant">{a.reaction}</p>
                  )}
                </div>
              </li>
            ))}
            {activeAlerts.map((a) => (
              <li
                key={a.id}
                className={`flex items-start gap-2 rounded-xl border px-3 py-2 ${
                  a.severity === 'critical' || a.severity === 'high'
                    ? 'border-ml-error/30 bg-ml-error/10'
                    : 'border-amber-200 bg-amber-50'
                }`}
              >
                <ShieldAlert
                  className={`mt-0.5 h-4 w-4 shrink-0 ${
                    a.severity === 'critical' || a.severity === 'high'
                      ? 'text-ml-error'
                      : 'text-amber-700'
                  }`}
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-ml-on-surface">
                    {a.title}
                    <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-ml-on-surface-variant/60">
                      {a.severity}
                    </span>
                  </p>
                  {a.message && <p className="text-xs text-ml-on-surface-variant">{a.message}</p>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  )
}

function Section({
  icon: Icon,
  title,
  children,
  onNavigate,
  navLabel,
  action,
}: {
  icon: React.ElementType
  title: string
  children: React.ReactNode
  onNavigate?: () => void
  navLabel?: string
  action?: React.ReactNode
}) {
  return (
    <section className="rounded-2xl border border-ml-outline-variant/15 bg-ml-surface-container-lowest p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-ml-primary" />
          <h3 className="text-xs font-bold uppercase tracking-widest text-ml-primary">{title}</h3>
        </div>
        <div className="flex items-center gap-3">
          {action}
          {onNavigate && (
            <button
              type="button"
              onClick={onNavigate}
              className="flex items-center gap-0.5 text-[11px] font-medium text-ml-on-surface-variant/70 transition hover:text-ml-primary"
            >
              {navLabel ?? 'Ver detalle'}
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
      {children}
    </section>
  )
}

function VitalTile({
  label,
  value,
  unit,
}: {
  label: string
  value: string | null
  unit: string
}) {
  return (
    <div className="rounded-xl border border-ml-outline-variant/15 bg-ml-surface px-3 py-2">
      <p className="text-[10px] font-bold uppercase tracking-wider text-ml-on-surface-variant/60">
        {label}
      </p>
      {value != null ? (
        <p className="mt-0.5 text-base font-semibold text-ml-on-surface">
          {value} <span className="text-[11px] font-normal text-ml-on-surface-variant/70">{unit}</span>
        </p>
      ) : (
        <p className="mt-0.5 text-sm italic text-ml-on-surface-variant/50">—</p>
      )}
    </div>
  )
}
